import { Search, SquarePen, X } from 'lucide-react';
import { Input, Tooltip } from '@strivelabs/ui-core/v2';
import { NEW_CHAT_SHORTCUT, useNewChat } from '../../hooks/useNewChat';

export interface ChatSearchProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * The chat list's header row: a compact search field with the new-chat action
 * beside it. The field is deliberately narrower than the panel — the list below
 * is the content, and a full-width field made the search look like the subject
 * of the tab rather than a way to narrow it.
 *
 * Debouncing lives in `useChatList`.
 */
export function ChatSearch({ value, onChange }: ChatSearchProps) {
  const startNewChat = useNewChat();

  return (
    <div className="flex items-center justify-end gap-1 px-0.5">
      <Input
        type="search"
        value={value}
        onChange={onChange}
        aria-label="Search chats"
        placeholder="Search chats…"
        leftIcon={<Search size={13} />}
        className="min-w-0 flex-1"
        // Only the size differs here — hover and focus now live on the Input.
        classNames={{ field: 'h-8', input: 'text-v2-sm' }}
        rightIcon={
          value ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onChange('')}
              className="relative grid size-4 place-items-center rounded-v2-sm text-v2-muted-foreground transition-colors before:absolute before:-inset-1.5 before:content-[''] hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring"
            >
              <X size={12} />
            </button>
          ) : undefined
        }
      />
      <Tooltip label="New chat" shortcut={NEW_CHAT_SHORTCUT} side="bottom">
        <button
          type="button"
          aria-label={`New chat (${NEW_CHAT_SHORTCUT})`}
          onClick={startNewChat}
          className="relative grid size-8 shrink-0 place-items-center rounded-v2-lg bg-v2-sidebar-accent text-v2-sidebar-foreground transition-[background-color,transform] before:absolute before:-inset-1 before:content-[''] hover:bg-v2-sidebar-border active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring motion-reduce:active:scale-100"
        >
          <SquarePen size={14} />
        </button>
      </Tooltip>
    </div>
  );
}
